// src/utils/packingBatchProductExport.js
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Export a packing batch's aggregated product list to Excel
 */
export const exportProductListToExcel = (products, batchNumber = '', filePrefix = 'packing-batch') => {
  try {
    const excelData = products.map((product, index) => ({
      '#': index + 1,
      'Product': product.displayName,
      'SKU': product.sku || '',
      'Total Quantity': product.totalQuantity,
      'Orders': product.ordersCount
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Product List');

    ws['!cols'] = [
      { wch: 5 },  // #
      { wch: 40 }, // Product
      { wch: 20 }, // SKU
      { wch: 15 }, // Total Quantity
      { wch: 10 }  // Orders
    ];

    const timestamp = new Date().toISOString().split('T')[0];
    const filename = batchNumber ? `${filePrefix}-${batchNumber}-product-list` : `${filePrefix}-product-list`;
    XLSX.writeFile(wb, `${filename}_${timestamp}.xlsx`);

    return { success: true, message: 'Excel file exported successfully' };
  } catch (error) {
    console.error('Excel export error:', error);
    return { success: false, message: 'Failed to export Excel file' };
  }
};

/**
 * Builds the jsPDF document for a packing batch's product list.
 * Shared by the PDF export and print flows so both stay in sync.
 *
 * NOTE: jspdf-autotable v4+ no longer patches `doc.autoTable(...)` — it must
 * be called as the imported `autoTable(doc, options)` function instead.
 */
export const buildProductListDocument = (products, batchNumber = '') => {
  const doc = new jsPDF('p', 'mm', 'a4');

  doc.setFontSize(16);
  doc.text(`Product List${batchNumber ? ` — Batch #${batchNumber}` : ''}`, 14, 15);

  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
  doc.text(`Total Products: ${products.length}`, 14, 27);

  const tableData = products.map((product, index) => [
    index + 1,
    product.displayName,
    product.sku || '',
    product.totalQuantity,
    product.ordersCount
  ]);

  autoTable(doc, {
    startY: 32,
    head: [['#', 'Product', 'SKU', 'Total Quantity', 'Orders']],
    body: tableData,
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    headStyles: {
      fillColor: [102, 126, 234],
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250]
    },
    columnStyles: {
      0: { cellWidth: 12 },
      1: { cellWidth: 90 },
      2: { cellWidth: 35 },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 20, halign: 'right' }
    },
    margin: { left: 14, right: 14 }
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  return doc;
};

/**
 * Export a packing batch's aggregated product list to PDF
 */
export const exportProductListToPDF = (products, batchNumber = '', filePrefix = 'packing-batch') => {
  try {
    const doc = buildProductListDocument(products, batchNumber);
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = batchNumber ? `${filePrefix}-${batchNumber}-product-list` : `${filePrefix}-product-list`;
    doc.save(`${filename}_${timestamp}.pdf`);

    return { success: true, message: 'PDF file exported successfully' };
  } catch (error) {
    console.error('PDF export error:', error);
    return { success: false, message: 'Failed to export PDF file' };
  }
};

/**
 * Print a packing batch's aggregated product list.
 *
 * Rather than printing the live app page (which drags in the sidebar,
 * header, and the rest of the batch details layout, and is fragile to get
 * right with print CSS), this opens the same PDF used for export in a new
 * tab and asks the browser to trigger its print dialog for it. Printing a
 * self-contained document sidesteps all of that entirely.
 */
export const printProductList = (products, batchNumber = '') => {
  try {
    const doc = buildProductListDocument(products, batchNumber);
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    const printWindow = window.open(blobUrl, '_blank');

    if (!printWindow) {
      return { success: false, message: 'Please allow pop-ups to print the product list' };
    }

    return { success: true };
  } catch (error) {
    console.error('Print product list error:', error);
    return { success: false, message: 'Failed to open print preview' };
  }
};
